<template>
  <div>
    <itprojdetails-tour v-if="$store.state.tour && !loading" />

    <!-- ============ Approve / Deny dialog ============ -->
    <transition name="menu-popover">
      <div v-if="overlay" class="lightbox" @click.self="closeLightbox" @keydown.esc="closeLightbox">
        <div class="decision-card" role="dialog" aria-modal="true" aria-labelledby="decision-title">
          <button type="button" class="dialog-close" aria-label="Close" @click="closeLightbox">&#215;</button>

          <h4 id="decision-title" class="mb5">Approve or deny project</h4>
          <p class="dialog-sub">
            #{{ project.id }} · {{ project.projName }}<br />
            Submitted by {{ project.submitterFullName || "n/a" }}, {{ project.submitterAC || "n/a" }}
          </p>

          <div class="segmented" role="radiogroup" aria-label="Decision">
            <button type="button" role="radio" :aria-checked="decision === 'Approved'"
              :class="['seg-btn', 'seg-approve', { active: decision === 'Approved' }]" @click="decision = 'Approved'">
              <span class="material-icons">check_circle</span> Approve
            </button>
            <button type="button" role="radio" :aria-checked="decision === 'Denied'"
              :class="['seg-btn', 'seg-deny', { active: decision === 'Denied' }]" @click="decision = 'Denied'">
              <span class="material-icons">cancel</span> Deny
            </button>
          </div>

          <label for="decisionReason" class="field-label">
            Reason <span v-if="decision === 'Denied'" class="req">required when denying</span>
            <span v-else class="opt">optional</span>
          </label>
          <textarea id="decisionReason" v-model="decisionReason" rows="6" class="form-control custom-textarea"
            :placeholder="`${UserFirstName}, explain your decision. We'll notify ${project.submitterAC || 'the submitter'}.`"></textarea>

          <div class="dialog-actions">
            <button type="button" class="btn-ghost" @click="closeLightbox">Cancel</button>
            <button type="button" class="list-btn submit-btn" :disabled="!canSubmitDecision" @click="ApproveOrDeny">
              {{ loading ? "Saving…" : decision ? `Submit ${decision === "Approved" ? "approval" : "denial"}` : "Choose approve or deny" }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ============ Header ============ -->
    <section class="py-5 pixelsbggrid bs-shadow mobile-top">
      <div class="container">
        <template v-if="loading && !project">
          <h1 class="text-white mb0 lh1">Loading project…</h1>
          <br />
          <div class="hollow-dots-spinner">
            <div class="dot"></div>
            <div class="dot"></div>
            <div class="dot"></div>
          </div>
        </template>
        <template v-else-if="project">
          <div class="hero-top">
            <span class="hero-id">Project #{{ project.id }}</span>
            <span :class="['status-pill', statusClass]">{{ project.status || "Pending" }}</span>
          </div>
          <h1 class="text-white mb0 lh1">{{ project.projName }}</h1>
          <p v-if="project.projDescription" class="hero-desc">{{ project.projDescription }}</p>
        </template>
      </div>
    </section>

    <div v-if="loading || !project" class="flex-box-wrap">
      <div class="flex-internal">
        <atom-spinner :animation-duration="1000" :size="40" :color="'#575757'" />
        <p>Loading…</p>
      </div>
    </div>

    <div v-else class="container mt20 mb20">
      <!-- Action bar -->
      <div class="action-bar">
        <BackButton />
        <button v-if="canDecide" type="button" class="list-btn decide-btn" @click="showOverlay">
          <span class="material-icons">gavel</span> Approve / Deny
        </button>
      </div>

      <div class="row">
        <!-- ============ Main column ============ -->
        <div class="col-lg-8">
          <!-- Decision -->
          <div class="card-back panel rounded bs-shadow">
            <div class="panel-head">
              <h4>Partner Project Approval</h4>
              <span :class="['status-pill', 'sm', statusClass]">{{ project.status || "Pending" }}</span>
            </div>
            <div class="field-grid">
              <div class="field">
                <div class="field-label"><span class="material-icons">person</span>Decided by</div>
                <div class="field-value">{{ project.approverFullName || "Not decided yet" }}</div>
              </div>
              <div class="field">
                <div class="field-label"><span class="material-icons">person</span>Primary contact</div>
                <div class="field-value">{{ project.primaryContact || "n/a" }}</div>
              </div>
              <div class="field">
                <div class="field-label"><span class="material-icons">calendar_month</span>Date of conversation</div>
                <div class="field-value">{{ fmtDate(project.conversationDate) }}</div>
              </div>
              <div class="field full">
                <div class="field-label"><span class="material-icons">text_snippet</span>Decision notes</div>
                <div class="field-value prewrap">{{ project.statusExplanation || "n/a" }}</div>
              </div>
            </div>
          </div>

          <!-- Job information -->
          <div id="itprojdetails-step-1" class="card-back panel rounded bs-shadow">
            <div class="panel-head">
              <h4>Job Information</h4>
              <span v-if="project.requestReason" class="reason-tag">{{ project.requestReason }}</span>
            </div>
            <div class="field-grid">
              <div v-for="f in jobFields" :key="f.label" class="field">
                <div class="field-label"><span class="material-icons">{{ f.icon }}</span>{{ f.label }}</div>
                <div class="field-value">{{ f.value || "n/a" }}</div>
              </div>
              <div class="field full">
                <div class="field-label"><span class="material-icons">inventory_2</span>Products</div>
                <div v-if="project.itjobProducts && project.itjobProducts.length" class="chips">
                  <span v-for="(part, i) in project.itjobProducts" :key="i" class="chip">{{ part.productId }}</span>
                </div>
                <div v-else class="field-value">n/a</div>
              </div>
              <div class="field full">
                <div class="field-label"><span class="material-icons">text_snippet</span>Project explanation</div>
                <div class="field-value prewrap">{{ project.projDescription || "n/a" }}</div>
              </div>
            </div>
          </div>

          <!-- Installation -->
          <div id="itprojdetails-step-3" class="card-back panel rounded bs-shadow">
            <div class="panel-head"><h4>Installation</h4></div>
            <div class="field-grid">
              <div class="field">
                <div class="field-label"><span class="material-icons">corporate_fare</span>Installation AC</div>
                <div class="field-value">{{ project.installerAC || "n/a" }}</div>
              </div>
              <div class="field full">
                <div class="field-label"><span class="material-icons">text_snippet</span>Note to AC</div>
                <div class="field-value prewrap">{{ project.installNotes || "n/a" }}</div>
              </div>
            </div>
          </div>

          <!-- Contacts: end user, GC, architect side by side -->
          <div class="contact-grid">
            <div v-for="c in contacts" :id="c.id" :key="c.id" class="card-back panel contact rounded bs-shadow">
              <div class="panel-head"><h5>{{ c.title }}</h5></div>
              <div v-if="c.name || c.company || c.phone || c.email || c.address" class="contact-body">
                <div v-if="c.name" class="contact-name">{{ c.name }}</div>
                <div v-if="c.company" class="contact-line">
                  <span class="material-icons">corporate_fare</span>{{ c.company }}
                </div>
                <a v-if="c.phone" :href="`tel:${c.phone}`" class="contact-line">
                  <span class="material-icons">phone</span>{{ c.phone }}
                </a>
                <a v-if="c.email" :href="`mailto:${c.email}`" class="contact-line">
                  <span class="material-icons">email</span>{{ c.email }}
                </a>
                <div v-if="c.address" class="contact-line">
                  <span class="material-icons">place</span>{{ c.address }}
                </div>
              </div>
              <p v-else class="text-muted mb0"><small>No information provided.</small></p>
            </div>
          </div>
        </div>

        <!-- ============ Sidebar ============ -->
        <div class="col-lg-4">
          <div id="itprojdetails-step-2" class="card-back panel rounded bs-shadow sticky-side">
            <div class="net-value pixelsbggrid bs-shadow">
              <div class="field-label">Net value</div>
              <div class="net-amount">{{ project.sscNetValue ? formatPrice(project.sscNetValue) : "n/a" }}</div>
            </div>
            <ul class="summary-list">
              <li>
                <span class="field-label"><span class="material-icons">tag</span>Project ID</span>
                <span>{{ project.id }}</span>
              </li>
              <li>
                <span class="field-label"><span class="material-icons">flag</span>Status</span>
                <span :class="['status-pill', 'sm', statusClass]">{{ project.status || "Pending" }}</span>
              </li>
              <li>
                <span class="field-label"><span class="material-icons">calendar_month</span>Order date (est)</span>
                <span>{{ fmtDate(project.orderDate) }}</span>
              </li>
              <li>
                <span class="field-label"><span class="material-icons">local_shipping</span>Ship date (est)</span>
                <span>{{ fmtDate(project.shipDate) }}</span>
              </li>
              <li>
                <span class="field-label"><span class="material-icons">corporate_fare</span>Submitting AC</span>
                <span class="text-right">{{ project.submitterAC || "n/a" }}</span>
              </li>
              <li>
                <span class="field-label"><span class="material-icons">corporate_fare</span>Installing AC</span>
                <span class="text-right">{{ project.installerAC || "n/a" }}</span>
              </li>
            </ul>
            <button v-if="canDecide" type="button" class="list-btn decide-btn w100" @click="showOverlay">
              <span class="material-icons">gavel</span> Approve / Deny
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { AtomSpinner } from "epic-spinners";
import itprojdetailsTour from "../components/Tours/itprojdetailsTour.vue";
import { formatCurrency } from "../utils/utils";
import BackButton from "@/components/Buttons/BackButton.vue";

// Read the token at request time so a fresh login is picked up
const auth = () => ({
  headers: { Authorization: "Bearer " + localStorage.getItem("SSCUSER") },
});

export default {
  name: "InterterritorialDetails",
  components: { AtomSpinner, BackButton, itprojdetailsTour },
  data() {
    return {
      ordermanageapi: import.meta.env.VITE_APP_ORDER_MANAGE_API,
      server: import.meta.env.VITE_APP_SERVER_URL,
      projectId: this.$route.params.id,
      userCompany: null,
      project: null,
      loading: false,
      overlay: false,
      decisionReason: "",
      decision: null, // "Approved" | "Denied" — nothing preselected
      userID: this.$store.state.user.UserId,
      UserFirstName: this.$store.state.user.FirstName,
      submitterEmail: "",
      salesRepEmail: "",
    };
  },
  computed: {
    canDecide() {
      const p = this.project;
      return !!p && p.installationACID == this.userCompany && !(p.approverFullName || "").length;
    },
    canSubmitDecision() {
      if (this.loading || !this.decision) return false;
      return this.decision === "Approved" || this.decisionReason.trim().length > 0;
    },
    statusClass() {
      const s = (this.project?.status || "").toLowerCase();
      if (s.startsWith("approv") || s.startsWith("accept")) return "is-approved";
      if (s.startsWith("den") || s.startsWith("reject")) return "is-denied";
      return "is-pending";
    },
    jobFields() {
      const p = this.project || {};
      return [
        { icon: "person", label: "Lead sales person", value: p.leadSalesPersonFullName },
        { icon: "person", label: "Submitted by", value: p.submitterFullName },
        { icon: "corporate_fare", label: "Area contractor submitted", value: p.submitterAC },
        { icon: "storefront", label: "Market", value: p.marketID },
      ];
    },
    contacts() {
      const p = this.project || {};
      const addr = (...parts) => {
        const [street, city, state, zip, county] = parts;
        const cityLine = [city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
        return [street, cityLine, county].filter(Boolean).join(", ");
      };
      return [
        {
          id: "itprojdetails-step-4", title: "End User / Customer",
          name: p.endUserName, phone: p.endUserPhone, email: p.endUserEmail, company: p.endUserCompany,
          address: addr(p.endUserStreet, p.endUserCity, p.endUserState, p.endUserZip),
        },
        {
          id: "itprojdetails-step-5", title: "General Contractor",
          name: p.contractorName, phone: p.contractorPhone, email: p.contractorEmail, company: p.contractorCompany,
          address: addr(p.contractorStreet, p.contractorCity, p.contractorState, p.contractorZip, p.contractorCounty),
        },
        {
          id: "itprojdetails-step-6", title: "Architect",
          name: p.architectName, phone: p.architectPhone, email: p.architectEmail, company: p.architectCompany,
          address: addr(p.architectStreet, p.architectCity, p.architectState, p.architectZip),
        },
      ];
    },
  },
  methods: {
    firstLoad() {
      this.loading = true;
      this.$store.commit("setActivePage", "order-manage");
      return axios
        .get(`${this.ordermanageapi}/itproj/${this.projectId}`, auth())
        .then(response => {
          this.project = response.data.projDetails[0];
          this.submitterEmail = this.project?.submitterEmail;
          this.salesRepEmail = this.project?.leadSalesPersonEmail;
          this.scrollTop();
        })
        .catch(error => {
          if (error.response?.status === 401) return this.$router.push("/");
          this.$toast.error("Couldn't load this project. Please try again.");
        })
        .finally(() => { this.loading = false; });
    },
    scrollTop() {
      this.$nextTick(() => {
        const el = document.getElementById("mobileScrollTarget");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      });
    },
    showOverlay() {
      this.decision = null;
      this.decisionReason = "";
      this.overlay = true;
    },
    closeLightbox() {
      if (!this.loading) this.overlay = false;
    },
    async ApproveOrDeny() {
      if (!this.canSubmitDecision) return;
      this.loading = true;

      const payload = {
        Status: this.decision,
        StatusExplanation: this.decisionReason.trim(),
        Approver: this.userID,
      };

      try {
        const updateResponse = await axios.post(`${this.ordermanageapi}/itproj/${this.projectId}`, payload, auth());
        this.project = updateResponse.data.projDetails[0];

        // Email notification (doesn't block the update)
        try {
          const emailPayload = {
            projectId: this.projectId,
            projectName: this.project.projName,
            status: this.decision,
            statusExplanation: this.decisionReason.trim() || null,
            approverName: this.project.approverFullName,
            approverEmail: this.project.approverEmail,
            leadSalesRepEmail: "spacenet3feedback@spacesaver.com",
            submitterEmail: this.project.submitterEmail,
            // leadSalesRepEmail: this.project.leadSalesPersonEmail
          };
          const emailResponse = await axios.post(`${this.server}/api/v1/send-itproject-notification`, emailPayload, auth());
          const sentCount = emailResponse.data.sentTo?.length || 0;
          this.$toast.success(`Project ${this.decision.toLowerCase()}: ${sentCount > 0 ? "notification sent" : "notification queued"}`);
        } catch (emailError) {
          console.warn("Email notification failed (non-blocking):", emailError.response?.data || emailError);
          this.$toast.warning(`Project updated, but the email notification failed. ${emailError.response?.data?.error || ""}`);
        }

        this.overlay = false;
        this.scrollTop();
      } catch (error) {
        console.error("Failed to update project:", error);
        this.$toast.error(error.response?.data?.error || "Failed to update project status");
      } finally {
        this.loading = false;
      }
    },
    fmtDate(d) {
      return d ? this.$moment(d).format("MM-DD-YYYY") : "n/a";
    },
    formatPrice(amount) {
      return formatCurrency(amount);
    },
    onKey(e) {
      if (e.key === "Escape" && this.overlay) this.closeLightbox();
    },
  },
  created() {
    this.$store.commit("showNavigation");
    this.userCompany = this.$store.state.user.CompanyID;
  },
  mounted() {
    this.scrollTop();
    this.firstLoad();
    window.addEventListener("keydown", this.onKey);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onKey);
  },
};
</script>

<style scoped>
.pixelsbggrid {
  background-color: #475059;
  background-size: cover;
  background-position: center;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1950 1200' preserveAspectRatio='xMidYMid slice'%3E%3Cdefs%3E%3CradialGradient id='a' cx='0' cy='1200' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%2356626d'/%3E%3Cstop offset='1' stop-color='%2356626d' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='b' cx='1950' cy='1200' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%2337778a'/%3E%3Cstop offset='1' stop-color='%2337778a' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='c' cx='975' cy='0' r='1200' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%232b5c60'/%3E%3Cstop offset='1' stop-color='%232b5c60' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='d' cx='975' cy='1200' r='1200' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23667787'/%3E%3Cstop offset='1' stop-color='%23667787' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='e' cx='0' cy='0' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%23475059'/%3E%3Cstop offset='1' stop-color='%23475059' stop-opacity='0'/%3E%3C/radialGradient%3E%3CradialGradient id='f' cx='1950' cy='0' r='800' gradientUnits='userSpaceOnUse'%3E%3Cstop offset='0' stop-color='%231a8182'/%3E%3Cstop offset='1' stop-color='%231a8182' stop-opacity='0'/%3E%3C/radialGradient%3E%3ClinearGradient id='base' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0' stop-color='%23475059'/%3E%3Cstop offset='.5' stop-color='%2356626d'/%3E%3Cstop offset='1' stop-color='%231a8182'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='%23475059' width='1950' height='1200'/%3E%3Crect fill='url(%23base)' width='1950' height='1200'/%3E%3Cline x1='0' y1='0' x2='0' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='50' y1='0' x2='50' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='100' y1='0' x2='100' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='150' y1='0' x2='150' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='200' y1='0' x2='200' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='250' y1='0' x2='250' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='300' y1='0' x2='300' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='350' y1='0' x2='350' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='400' y1='0' x2='400' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='450' y1='0' x2='450' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='500' y1='0' x2='500' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='550' y1='0' x2='550' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='600' y1='0' x2='600' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='650' y1='0' x2='650' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='700' y1='0' x2='700' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='750' y1='0' x2='750' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='800' y1='0' x2='800' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='850' y1='0' x2='850' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='900' y1='0' x2='900' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='950' y1='0' x2='950' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1000' y1='0' x2='1000' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='1050' y1='0' x2='1050' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1100' y1='0' x2='1100' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1150' y1='0' x2='1150' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1200' y1='0' x2='1200' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1250' y1='0' x2='1250' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='1300' y1='0' x2='1300' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1350' y1='0' x2='1350' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1400' y1='0' x2='1400' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1450' y1='0' x2='1450' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1500' y1='0' x2='1500' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='1550' y1='0' x2='1550' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1600' y1='0' x2='1600' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1650' y1='0' x2='1650' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1700' y1='0' x2='1700' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1750' y1='0' x2='1750' y2='1200' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='1800' y1='0' x2='1800' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1850' y1='0' x2='1850' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='1900' y1='0' x2='1900' y2='1200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='0' x2='1950' y2='0' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='50' x2='1950' y2='50' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='100' x2='1950' y2='100' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='150' x2='1950' y2='150' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='200' x2='1950' y2='200' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='250' x2='1950' y2='250' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='300' x2='1950' y2='300' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='350' x2='1950' y2='350' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='400' x2='1950' y2='400' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='450' x2='1950' y2='450' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='500' x2='1950' y2='500' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='550' x2='1950' y2='550' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='600' x2='1950' y2='600' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='650' x2='1950' y2='650' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='700' x2='1950' y2='700' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='750' x2='1950' y2='750' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='800' x2='1950' y2='800' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='850' x2='1950' y2='850' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='900' x2='1950' y2='900' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='950' x2='1950' y2='950' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='1000' x2='1950' y2='1000' stroke='%23fff' stroke-opacity='0.12' stroke-width='2'/%3E%3Cline x1='0' y1='1050' x2='1950' y2='1050' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='1100' x2='1950' y2='1100' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cline x1='0' y1='1150' x2='1950' y2='1150' stroke='%23fff' stroke-opacity='0.05' stroke-width='1'/%3E%3Cg fill-opacity='0.45'%3E%3Crect fill='url(%23a)' width='1950' height='1200'/%3E%3Crect fill='url(%23b)' width='1950' height='1200'/%3E%3Crect fill='url(%23c)' width='1950' height='1200'/%3E%3Crect fill='url(%23d)' width='1950' height='1200'/%3E%3Crect fill='url(%23e)' width='1950' height='1200'/%3E%3Crect fill='url(%23f)' width='1950' height='1200'/%3E%3C/g%3E%3C/svg%3E");
}
/* ---------- Shared ---------- */
.card-back { background: #fff; }
.panel { padding: 20px; margin-bottom: 15px; }
.w100 { width: 100%; }
.prewrap { white-space: pre-line; }
.text-right { text-align: right; }
.mt20 { margin-top: 20px; }

.flex-box-wrap { display: flex; justify-content: center; align-items: center; padding: 80px 0; }

/* ---------- Header ---------- */
.hero-top { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; flex-wrap: wrap; }
.hero-id { color: rgba(255, 255, 255, 0.8); font-size: 16px; letter-spacing: 0.02em; }
.hero-desc { color: rgba(255, 255, 255, 0.85); margin: 12px 0 0; max-width: 75ch; line-height: 1.4; }

.status-pill {
  display: inline-flex; align-items: center; padding: 4px 12px; border-radius: 999px;
  font-size: 13px; font-weight: 500; line-height: 1.4; white-space: nowrap;
}
.status-pill.sm { padding: 2px 10px; font-size: 12px; }
.is-approved { background: #e3f6ee; color: #1d7a4f; }
.is-denied { background: #fde8e8; color: #b42318; }
.is-pending { background: #eceef1; color: #4d5967; }

/* ---------- Action bar ---------- */
.action-bar { display: flex; align-items: center; gap: 10px; margin-bottom: 15px; }
.decide-btn {
  margin-left: auto; display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  border: none; padding: 8px 14px; line-height: 1.2;
}
.decide-btn .material-icons { font-size: 18px; }
.sticky-side .decide-btn { margin: 15px 0 0; }

/* ---------- Panels ---------- */
.panel-head {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  border-bottom: 1px solid #eee; padding-bottom: 12px; margin-bottom: 15px;
  padding: 14px 0;
}
.panel-head h4, .panel-head h5 { margin: 0; }
.reason-tag {
  margin-left: auto; background: #e6f4f5; color: #2f7d86; border-radius: 4px;
  padding: 4px 10px; font-size: 13px;
}
.panel-head .status-pill { margin-left: auto; }

/* Label / value pairs in two columns */
.field-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px 24px; }
.field.full { grid-column: 1 / -1; }
.field-label {
  display: flex; align-items: center; gap: 5px; color: #575757;
  font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; margin-bottom: 4px;
}
.field-label .material-icons { font-size: 16px; color: #8a9099; }
.field-value { color: #333; font-size: 15px; line-height: 1.4; overflow-wrap: anywhere; }

.chips { display: flex; flex-wrap: wrap; gap: 6px; }
.chip { background: #eef2f5; color: #3e4651; border-radius: 4px; padding: 4px 10px; font-size: 14px; }

/* ---------- Contact cards ---------- */
.contact-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 15px; margin-bottom: 15px; }
.contact-grid .panel { margin-bottom: 0; }
.contact-name { font-size: 16px; font-weight: 600; color: #333; margin-bottom: 6px; }
.contact-line {
  display: flex; align-items: flex-start; gap: 6px; color: #575757; font-size: 14px;
  line-height: 1.4; margin-bottom: 4px; overflow-wrap: anywhere;
}
.contact-line .material-icons { font-size: 16px; color: #8a9099; margin-top: 1px; }
.contact-line.link { color: #2f7d86; text-decoration: none; }
.contact-line.link:hover { color: #00b4b4; text-decoration: underline; }

/* ---------- Sidebar ---------- */
@media (min-width: 992px) {
  .sticky-side { position: sticky; top: 20px; }
}
.net-value { color: #fff; border-radius: 4px; padding: 16px; margin-bottom: 10px; }
.net-value .field-label { color: rgba(255, 255, 255, 0.75); }
.net-amount { font-size: 28px; font-weight: 600; line-height: 1.1; font-variant-numeric: tabular-nums; }
.summary-list { list-style: none; padding: 0; margin: 0; }
.summary-list li {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 10px 0; border-bottom: 1px solid #eee; font-size: 15px; color: #333;
}
.summary-list li:last-child { border-bottom: none; }
.summary-list .field-label { margin-bottom: 0; }

/* ---------- Decision dialog ---------- */
.lightbox {
  position: fixed; inset: 0; z-index: 10003; background: rgba(0, 0, 0, 0.45);
  display: flex; align-items: center; justify-content: center; padding: 16px;
}
.decision-card {
  position: relative; background: #fff; border-radius: 6px; width: 100%; max-width: 520px;
  padding: 24px; text-align: left; box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
}
.dialog-close {
  position: absolute; top: 8px; right: 12px; border: none; background: none;
  font-size: 28px; line-height: 1; color: #777; cursor: pointer;
}
.dialog-close:hover { color: #333; }
.dialog-sub { color: #777; font-size: 14px; line-height: 1.4; margin-bottom: 16px; }

.segmented { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 16px; }
.seg-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  border: 2px solid #dde3e8; background: #fff; color: #575757; border-radius: 6px;
  padding: 10px; font-size: 15px; cursor: pointer; transition: all 0.15s ease;
}
.seg-btn .material-icons { font-size: 20px; }
.seg-approve.active { border-color: #1d7a4f; background: #e3f6ee; color: #1d7a4f; }
.seg-deny.active { border-color: #b42318; background: #fde8e8; color: #b42318; }
.seg-btn:focus-visible { outline: 2px solid #429da8; outline-offset: 2px; }

.decision-card .field-label { text-transform: none; letter-spacing: 0; font-size: 14px; }
.req { color: #b42318; font-weight: 400; margin-left: 6px; }
.opt { color: #999; font-weight: 400; margin-left: 6px; }
.custom-textarea {
  width: 100%; padding: 12px; border-radius: 4px; border: 1px solid #dde3e8;
  background: #f5f8fa; color: #000; resize: vertical;
}
.custom-textarea:focus { outline: none; border-color: #429da8; box-shadow: 0 0 0 3px rgba(66, 157, 168, 0.2); }
textarea::placeholder { color: #808080; opacity: 1; }

.dialog-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px; }
.btn-ghost { border: none; background: none; color: #575757; padding: 8px 12px; cursor: pointer; }
.btn-ghost:hover { color: #000; }
.submit-btn { border: none; padding: 8px 16px; line-height: 1.4; }
.submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* ---------- Small screens ---------- */
@media (max-width: 767px) {
  .field-grid { grid-template-columns: 1fr; }
  .decision-card { padding: 20px 16px; }
  .net-amount { font-size: 24px; }
}
</style>
